import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, HelpCircle, ArrowRight } from 'lucide-react';

interface CoachMark {
  id: string;
  title: string;
  description: string;
  targetSelector: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

const coachMarks: CoachMark[] = [
  {
    id: 'upload-area',
    title: 'Upload Media',
    description: 'Drag and drop your media files here or click to browse. You can upload multiple files at once.',
    targetSelector: '.upload-area',
    position: 'bottom'
  },
  {
    id: 'platform-toggles',
    title: 'Select Platforms',
    description: 'Choose which social media platforms you want to post to. We support Twitter, Facebook, Instagram, LinkedIn, YouTube, and TikTok (coming soon).',
    targetSelector: '.platform-toggles',
    position: 'bottom'
  },
  {
    id: 'caption-editor',
    title: 'Write Captions',
    description: 'Create your post content here. Character limits are shown for each platform.',
    targetSelector: '.caption-editor',
    position: 'right'
  },
  {
    id: 'ai-tools',
    title: 'AI Assistance',
    description: 'Use our AI tools to help create engaging content, generate hashtags, and more.',
    targetSelector: '.ai-tools',
    position: 'left'
  },
  {
    id: 'schedule-options',
    title: 'Schedule Your Post',
    description: 'Choose when to publish your content - now, at a specific time, or on a recurring schedule.',
    targetSelector: '.schedule-options',
    position: 'top'
  }
];

const CoachMarksTour: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMarkIndex, setCurrentMarkIndex] = useState(0);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });

  useEffect(() => {
    // Check if this is the first time the user is visiting
    const hasSeenTour = localStorage.getItem('hasSeenMultiPlatformTour');
    if (!hasSeenTour) {
      // Wait for the components to render
      setTimeout(() => {
        setIsOpen(true);
      }, 1000);
    }
  }, []);

  useEffect(() => {
    if (isOpen && coachMarks[currentMarkIndex]) {
      const targetElement = document.querySelector(coachMarks[currentMarkIndex].targetSelector);
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        setPosition({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          height: rect.height
        });
      }
    }
  }, [isOpen, currentMarkIndex]);

  const handleNext = () => {
    if (currentMarkIndex < coachMarks.length - 1) {
      setCurrentMarkIndex(currentMarkIndex + 1);
    } else {
      handleClose();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('hasSeenMultiPlatformTour', 'true');
  };

  const handleRestart = () => {
    setCurrentMarkIndex(0);
    setIsOpen(true);
  };

  if (!isOpen) {
    return (
      <button
        onClick={handleRestart}
        className="fixed bottom-4 right-4 p-2 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 transition-colors z-50"
        aria-label="Start tour"
      >
        <HelpCircle className="h-5 w-5" />
      </button>
    );
  }

  const currentMark = coachMarks[currentMarkIndex];
  if (!currentMark) return null;

  // Calculate tooltip position based on target element and specified position
  const getTooltipStyle = () => {
    const tooltipWidth = 300;
    const tooltipHeight = 150;
    const margin = 20;

    switch (currentMark.position) {
      case 'top':
        return {
          top: position.top - tooltipHeight - margin,
          left: position.left + (position.width / 2) - (tooltipWidth / 2)
        };
      case 'bottom':
        return {
          top: position.top + position.height + margin,
          left: position.left + (position.width / 2) - (tooltipWidth / 2)
        };
      case 'left':
        return {
          top: position.top + (position.height / 2) - (tooltipHeight / 2),
          left: position.left - tooltipWidth - margin
        };
      case 'right':
        return {
          top: position.top + (position.height / 2) - (tooltipHeight / 2),
          left: position.left + position.width + margin
        };
      default:
        return {
          top: position.top + position.height + margin,
          left: position.left
        };
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-40"
            onClick={handleClose}
          />

          {/* Spotlight on target element */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 pointer-events-none"
            style={{
              maskImage: `radial-gradient(circle at ${position.left + position.width / 2}px ${position.top + position.height / 2}px, transparent 0, transparent ${Math.max(position.width, position.height) / 2}px, black ${Math.max(position.width, position.height) / 2 + 1}px)`,
              WebkitMaskImage: `radial-gradient(circle at ${position.left + position.width / 2}px ${position.top + position.height / 2}px, transparent 0, transparent ${Math.max(position.width, position.height) / 2}px, black ${Math.max(position.width, position.height) / 2 + 1}px)`
            }}
          />

          {/* Target highlight */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed z-50 pointer-events-none"
            style={{
              top: position.top,
              left: position.left,
              width: position.width,
              height: position.height,
              boxShadow: '0 0 0 2px rgba(99, 102, 241, 1)'
            }}
          />

          {/* Tooltip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed z-50 bg-white dark:bg-dark-800 rounded-lg shadow-xl p-4 w-[300px]"
            style={getTooltipStyle()}
          >
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {currentMark.title}
              </h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {currentMark.description}
            </p>
            <div className="flex justify-between items-center">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {currentMarkIndex + 1} of {coachMarks.length}
              </div>
              <button
                onClick={handleNext}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm flex items-center"
              >
                {currentMarkIndex < coachMarks.length - 1 ? 'Next' : 'Finish'}
                <ArrowRight className="ml-1 h-4 w-4" />
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CoachMarksTour;